import type {
  Coverage,
  OpenRequirement,
  PersonaProductEvaluation,
  RelevancyRating,
  ReportSnapshot,
  RequirementViolation,
  ServerBand,
} from "../../api/domain.ts";
import { relevancyWeight } from "../../api/domain.ts";

/**
 * Turning one evaluation payload into the rows the report screen renders.
 *
 * Nothing here computes a score, a band or a coverage: the server already did
 * that. This module only names identities, orders rows and pairs a persona's
 * verdicts with the products they judge.
 */

/** A product, topic, capability or persona identity paired with its label. */
export type Named = {
  id: string;
  label: string;
};

const nameFrom = (entries: Named[], id: string): string =>
  entries.find((entry) => entry.id === id)?.label ?? id;

export const productNames = (snapshot: ReportSnapshot): Named[] =>
  snapshot.input.products.map((product) => ({ id: product.id, label: product.name }));

export const topicNames = (snapshot: ReportSnapshot): Named[] =>
  snapshot.input.topics.map((topic) => ({ id: topic.id, label: topic.title }));

export const capabilityNames = (snapshot: ReportSnapshot): Named[] =>
  snapshot.input.capabilities.map((capability) => ({
    id: capability.id,
    label: capability.label,
  }));

export const personaNames = (snapshot: ReportSnapshot): Named[] =>
  snapshot.input.personas.map((persona) => ({ id: persona.id, label: persona.name }));

export type RankedProduct = {
  product: string;
  name: string;
  score: number | null;
  coverage: Coverage;
};

/**
 * Products best first. A product without a score has nothing to rank on, so it
 * goes last rather than counting as a zero.
 */
export const rankProducts = (snapshot: ReportSnapshot): RankedProduct[] => {
  const names = productNames(snapshot);

  return snapshot.evaluation.products
    .map((product) => ({
      product: product.product,
      name: nameFrom(names, product.product),
      score: product.score,
      coverage: product.coverage,
    }))
    .sort((left, right) => {
      if (left.score === null && right.score === null) return left.name.localeCompare(right.name);
      if (left.score === null) return 1;
      if (right.score === null) return -1;
      return right.score - left.score;
    });
};

export type TopicCell = {
  product: string;
  score: number | null;
  band: ServerBand | null;
  coverage: Coverage;
};

export type TopicRow = {
  topic: string;
  title: string;
  weight: number;
  cells: TopicCell[];
};

/**
 * One row per topic, one cell per product in ranking order. A product that does
 * not carry the topic leaves an empty cell instead of a zero.
 */
export const topicRows = (snapshot: ReportSnapshot): TopicRow[] => {
  const ranked = rankProducts(snapshot);
  const names = topicNames(snapshot);
  const byProduct = new Map(
    snapshot.evaluation.products.map((product) => [product.product, product]),
  );

  return snapshot.input.topics.map((topic) => {
    const first = byProduct.get(ranked[0]?.product ?? "");
    const weight =
      first?.topics.find((entry) => entry.topic === topic.id)?.effective_weight ?? topic.relevancy;

    return {
      topic: topic.id,
      title: nameFrom(names, topic.id),
      weight,
      cells: ranked.map((product) => {
        const evaluated = byProduct
          .get(product.product)
          ?.topics.find((entry) => entry.topic === topic.id);

        return {
          product: product.product,
          score: evaluated?.score ?? null,
          band: evaluated?.band ?? null,
          coverage: evaluated?.coverage ?? { answered: 0, total: 0 },
        };
      }),
    };
  });
};

export type PersonaEntry = {
  product: string;
  name: string;
  /** The global score, kept for context and comparability across personas. */
  score: number | null;
  /** What this persona's ranking is built on, and what its band shows. */
  weightedScore: number | null;
  violations: RequirementViolation[];
  open: OpenRequirement[];
  excluded: boolean;
};

/** One rating this persona applied, with the weight it maps to. */
export type AppliedWeight = {
  capability: string;
  label: string;
  rating: RelevancyRating;
  weight: number;
};

export type PersonaColumn = {
  persona: string;
  name: string;
  /** The persona's own ranking, in the order the engine produced it. */
  entries: PersonaEntry[];
  /** The ratings behind that ranking, so the skew is auditable. */
  weights: AppliedWeight[];
  /**
   * Whether this persona orders the products differently from the global
   * ranking. A reader comparing the two columns needs telling, because the
   * numbers alone do not say which weighting moved what.
   */
  reordered: boolean;
};

/**
 * The score a persona's ranking sorts by.
 *
 * The weighted score is the number. The global one stands in where a snapshot
 * predates persona weighting, which mirrors `PersonaProductEvaluation::ranked`
 * in the core.
 */
const rankedScore = (entry: PersonaProductEvaluation): number | null =>
  entry.weighted_score ?? entry.score;

/** Personas side by side, each with its own ranking; they are never blended. */
export const personaColumns = (snapshot: ReportSnapshot): PersonaColumn[] => {
  const products = productNames(snapshot);
  const personas = personaNames(snapshot);
  const capabilities = capabilityNames(snapshot);
  const globalOrder = rankProducts(snapshot).map((product) => product.product);

  return snapshot.evaluation.personas.map((persona) => {
    const entries = persona.products.map((entry: PersonaProductEvaluation) => ({
      product: entry.product,
      name: nameFrom(products, entry.product),
      score: entry.score,
      weightedScore: rankedScore(entry),
      violations: entry.violations,
      open: entry.open,
      excluded: entry.excluded,
    }));

    return {
      persona: persona.persona,
      name: nameFrom(personas, persona.persona),
      entries,
      weights: (snapshot.input.relevancies ?? [])
        .filter((relevancy) => relevancy.persona === persona.persona && relevancy.active)
        .map((relevancy) => ({
          capability: relevancy.capability,
          label: nameFrom(capabilities, relevancy.capability),
          rating: relevancy.rating,
          weight: relevancyWeight(relevancy.rating),
        })),
      reordered: entries.some(
        (entry, position) => entry.product !== globalOrder[position],
      ),
    };
  });
};

/** The capability a violation or open requirement talks about, by name. */
export const capabilityLabel = (snapshot: ReportSnapshot, capability: string): string =>
  nameFrom(capabilityNames(snapshot), capability);

/** Every capability of one product with its answers, for the drill-down. */
export type CapabilityRow = {
  capability: string;
  label: string;
  topic: string;
  score: number | null;
  band: ServerBand | null;
  coverage: Coverage;
  answers: {
    answer: string;
    question: string;
    value: number;
    source: string;
    confidence: string;
    author: string;
  }[];
};

export const capabilityRows = (snapshot: ReportSnapshot, product: string): CapabilityRow[] => {
  const names = capabilityNames(snapshot);
  const topics = topicNames(snapshot);
  const evaluated = snapshot.evaluation.products.find((entry) => entry.product === product);
  if (evaluated === undefined) return [];

  return evaluated.topics.flatMap((topic) =>
    topic.capabilities.map((capability) => ({
      capability: capability.capability,
      label: nameFrom(names, capability.capability),
      topic: nameFrom(topics, topic.topic),
      score: capability.score,
      band: capability.band,
      coverage: capability.coverage,
      answers: capability.answers.map((answer) => ({
        answer: answer.answer,
        question: answer.question,
        value: answer.value,
        source: answer.source,
        confidence: answer.confidence,
        author: answer.author,
      })),
    })),
  );
};
